import { LitElement, css, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { nsSharedStyles } from '../ns-styles/index.js';
import { formatCredits, parseCreditsAmount } from '../helpers/public-ledger-credits.js';

import './ns-input-field.js';

export interface CreditsChangeDetail {
  // The whole number of credits typed, or null when the text is empty or isn't one
  credits: number | null;
  text: string;
}

/**
 * An amount of nodeStream credits, typed as a whole number (grouping commas allowed). Set `.credits` to prefill it (the
 * suggested amount), `max` to show how many are available, and `error` to show a refusal under the field. Each edit fires
 * `credits-change` with `{credits, text}`; `credits` is null until the text is a whole number. It checks only the format:
 * the card and its lambda decide whether the amount is allowed.
 */
@customElement('ns-credits-input')
export class NSCreditsInput extends LitElement {
  @property({ type: String })
  label = 'Amount (credits)';

  @property({ attribute: false })
  credits: number | null = null;

  // How many credits are available, shown as a hint under the field; null shows none
  @property({ attribute: false })
  max: number | null = null;

  @property({ type: String })
  error = '';

  @property({ type: Boolean })
  disabled = false;

  @property({ type: Boolean })
  required = false;

  @state()
  private __text = '';

  static styles = [
    nsSharedStyles,
    css`
      :host {
        display: block;
      }

      :host([hidden]) {
        display: none;
      }

      .hint {
        margin: var(--wa-space-2xs) 0 0;
        font-size: var(--wa-font-size-s);
        font-weight: 300;
      }
    `,
  ];

  willUpdate(changed: PropertyValues<this>) {
    // A credits value set from outside replaces the text, unless it is what the text already says
    if (changed.has('credits') && parseCreditsAmount(this.__text) !== this.credits) {
      this.__text = this.credits === null ? '' : String(this.credits);
    }
  }

  private __onInput(e: Event) {
    const text = (e.target as unknown as { value?: string }).value ?? '';
    this.__text = text;
    const credits = parseCreditsAmount(text);
    this.credits = credits;
    this.dispatchEvent(
      new CustomEvent<CreditsChangeDetail>('credits-change', {
        detail: { credits, text },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <ns-input-field
        stacked
        type="text"
        ?required=${this.required}
        ?disabled=${this.disabled}
        label=${this.required ? `${this.label}*` : this.label}
        placeholder="Enter Here"
        .value=${this.__text}
        @input=${(e: Event) => this.__onInput(e)}
      ></ns-input-field>
      ${this.max !== null
        ? html`
            <p class="hint">Up to ${formatCredits(this.max)} credits available</p>
          `
        : nothing}
      ${this.error
        ? html`
            <div class="error-text" role="alert">${this.error}</div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-credits-input': NSCreditsInput;
  }
}
